import { useTranslation } from 'react-i18next';
import { SupportedFundImpactDtoIn, SupportedIsraeliIdChangeImpactDtoIn } from '@/models/in/SupportedIsraeliIdChangeImpactDtoIn';
import { managementSupportedHref } from './israeliIdChangeBlocker';
import styles from './IsraeliIdChangeImpact.module.css';

interface IsraeliIdChangeImpactProps {
  impact: SupportedIsraeliIdChangeImpactDtoIn;
  contactId: number;
  supportedName: string;
  currentIsraeliId: string;
  newIsraeliId: string;
  /**
   * Say so when a coordinator's request for this supported waits for the office. Off in the review
   * of that very request.
   */
  showPendingRequest?: boolean;
  /** Opens that waiting request, to decide it instead of editing past it. */
  onOpenPendingRequest?: () => void;
}

/**
 * What the office reads before a supported's Israeli ID changes (HAT-1428): who this is, what will
 * happen, and every place the supported is - Druk asked for all of them, since the ID belongs to the
 * person and not to one fund. Shared by the direct edit and the review of a coordinator's request.
 */
const IsraeliIdChangeImpact = ({
  impact, contactId, supportedName, currentIsraeliId, newIsraeliId, showPendingRequest = true, onOpenPendingRequest,
}: IsraeliIdChangeImpactProps) => {
  const { t } = useTranslation();
  const prefix = 'supportedsManagement.editSupportedDialog.impact';

  const relationshipLabel = (relationship: string) =>
    t(`${prefix}.relationship.${relationship}`, { defaultValue: t(`${prefix}.relationship.other`) });

  const fundRequests = (fund: SupportedFundImpactDtoIn) => {
    const parts = [
      fund.activeSupportRequestsCount > 0 && t(`${prefix}.activeRequests`, { count: fund.activeSupportRequestsCount }),
      fund.paidSupportRequestsCount > 0 && t(`${prefix}.paidSupports`, { count: fund.paidSupportRequestsCount }),
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : t(`${prefix}.noRequests`);
  };

  const link = (id: number, label: string) => (
    <a className={styles.link} href={managementSupportedHref(id)} target="_blank" rel="noopener noreferrer">
      {label} ↗
    </a>
  );

  return (
    <div className={styles.impact}>
      <div className={styles.who}>
        <span className={styles.name}>{supportedName}</span>
        <span>
          <bdi className={styles.idValue}>{currentIsraeliId}</bdi>
          {' ← '}
          <bdi className={styles.idValue}>{newIsraeliId}</bdi>
        </span>
        {link(contactId, t(`${prefix}.openSupported`))}
      </div>

      <p>{t(`${prefix}.whatHappens`, { name: supportedName })}</p>

      <p className={styles.heading}>{t(`${prefix}.fundsHeading`, { count: impact.funds.length })}</p>
      <ul className={styles.list}>
        {impact.funds.map((fund) => (
          <li key={fund.fundId}>
            <span className={styles.fund}>
              {fund.fundName ? `${fund.fundName} (${fund.fundId})` : fund.fundId}
            </span>
            {' · '}
            {fundRequests(fund)}
          </li>
        ))}
      </ul>

      {impact.familyOf.map((relative) => (
        <p key={relative.contactId}>
          {t(`${prefix}.familyOf`, {
            relationship: relationshipLabel(relative.relationship),
            name: `${relative.firstName} ${relative.lastName}`.trim(),
            funds: relative.fundIds.join(', ') || '-',
          })}{' '}
          {link(relative.contactId, t(`${prefix}.openRelative`))}
        </p>
      ))}

      {showPendingRequest && impact.pendingRequest && (
        <p className={styles.pending}>
          {t(`${prefix}.pendingRequest`, { id: impact.pendingRequest.requestedIsraeliId })}{' '}
          {onOpenPendingRequest && (
            <button type="button" className={styles.linkButton} onClick={onOpenPendingRequest}>
              {t(`${prefix}.openPendingRequest`)}
            </button>
          )}
        </p>
      )}

      <p className={styles.muted}>
        {t(`${prefix}.stays`, {
          paymentInfos: impact.paymentInfosCount,
          classifications: impact.classificationsCount,
          children: impact.childrenCount,
        })}
      </p>
    </div>
  );
};

export default IsraeliIdChangeImpact;
